/* Mobile paired-design carousel. Desktop remains a three-column catalog. */
(() => {
  const mobile = window.matchMedia('(max-width: 749px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new WeakMap();

  const init = (scope = document) => {
    scope.querySelectorAll('[data-reversible-carousel]').forEach(root => {
      if (instances.has(root)) return;
      const track = root.querySelector('[data-reversible-track]');
      const controls = root.querySelector('[data-reversible-controls]');
      if (!track || !controls) return;
      const cards = [...track.children];
      if (cards.length < 2) return;
      const previous = controls.querySelector('[data-reversible-prev]');
      const next = controls.querySelector('[data-reversible-next]');
      const count = controls.querySelector('[data-reversible-count]');
      if (!previous || !next || !count) return;
      const events = new AbortController();
      const options = { signal: events.signal };
      let frame = 0;
      let current = 0;
      let target = null;

      const positions = () => {
        const start = track.getBoundingClientRect().left;
        const limit = Math.max(0, track.scrollWidth - track.clientWidth);
        return cards.map(card => Math.min(limit, Math.max(0, card.getBoundingClientRect().left - start + track.scrollLeft)));
      };
      const update = () => {
        frame = 0;
        const limit = track.scrollWidth - track.clientWidth;
        const active = mobile.matches && limit > 1;
        controls.hidden = !active;
        if (!active) {
          track.removeAttribute('tabindex');
          target = null;
          return;
        }
        track.tabIndex = 0;
        const points = positions();
        current = points.reduce((best, point, index) => Math.abs(point - track.scrollLeft) < Math.abs(points[best] - track.scrollLeft) ? index : best, 0);
        if (track.scrollLeft >= limit - 2) current = cards.length - 1;
        previous.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= limit - 2;
        const label = `${current + 1} / ${cards.length}`;
        if (count.textContent !== label) count.textContent = label;
        if (target !== null && Math.abs(points[target] - track.scrollLeft) <= 2) target = null;
      };
      const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
      const goTo = index => {
        target = Math.max(0, Math.min(cards.length - 1, index));
        track.scrollTo({ left: positions()[target], behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      };
      previous.addEventListener('click', () => goTo((target ?? current) - 1), options);
      next.addEventListener('click', () => goTo((target ?? current) + 1), options);
      track.addEventListener('scroll', schedule, { ...options, passive: true });
      track.addEventListener('pointerdown', () => { target = null; }, options);
      track.addEventListener('wheel', () => { target = null; }, { ...options, passive: true });
      track.addEventListener('keydown', event => {
        if (!mobile.matches || event.target !== track) return;
        const keys = { ArrowLeft: (target ?? current) - 1, ArrowRight: (target ?? current) + 1, Home: 0, End: cards.length - 1 };
        if (!Object.hasOwn(keys, event.key)) return;
        event.preventDefault();
        goTo(keys[event.key]);
      }, options);
      root.addEventListener('shopify:block:select', event => {
        if (!mobile.matches) return;
        const index = cards.findIndex(card => card === event.target || card.contains(event.target));
        if (index >= 0) goTo(index);
      }, options);
      const observer = new ResizeObserver(schedule);
      observer.observe(track);
      mobile.addEventListener('change', schedule, options);
      instances.set(root, () => {
        events.abort();
        observer.disconnect();
        cancelAnimationFrame(frame);
        instances.delete(root);
      });
      update();
    });
  };
  init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-reversible-carousel]').forEach(root => instances.get(root)?.());
  });
})();
